import { useEffect, useState } from "react";
import { fetchEatAndPlayMenu, EAT_AND_PLAY_PAYLOAD_KEYS } from "src/api/payload/eatAndPlay";
import { isPayloadSource } from "src/utils/hubSource";

/**
 * Loads an Eat & Play menu from Payload when `eat-and-play` is in
 * REACT_APP_PAYLOAD_SOURCES. Fetches the tab matching `categoryKey`
 * (`cashMenu`, `cashBreakfast`, `tournamentMenu`, `tournamentBreakfast`).
 *
 * - Flag off / unknown tab → `source: "static"` (legacy JSON is OK).
 * - Flag on + empty CMS or API error → `menu` null, `source: "payload"`,
 *   `error` set — do NOT fall back to static (stale prices / unavailable items).
 *
 * @param {Object} options
 * @param {string} options.categoryKey - Tab key (`cashMenu`, `cashBreakfast`, …)
 * @param {string} options.locale - i18n language
 * @param {boolean} [options.skip] - Do not fetch (caller already has a mapped menu)
 * @returns {{
 *   menu: import('src/utils/mapEatAndPlay').EatAndPlayMenu | null,
 *   loading: boolean,
 *   error: string | null,
 *   source: 'payload' | 'static',
 * }}
 */
export function useEatAndPlayMenu({ categoryKey, locale, skip = false }) {
  const normalizedLocale = locale === "fr" ? "fr" : "en";
  const shouldFetch =
    !skip &&
    EAT_AND_PLAY_PAYLOAD_KEYS.includes(categoryKey) &&
    isPayloadSource("eat-and-play");

  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(shouldFetch);
  const [error, setError] = useState(null);
  const [source, setSource] = useState(shouldFetch ? "payload" : "static");

  useEffect(() => {
    if (!shouldFetch) {
      setMenu(null);
      setLoading(false);
      setError(null);
      setSource("static");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSource("payload");

    fetchEatAndPlayMenu(normalizedLocale, categoryKey)
      .then((next) => {
        if (cancelled) return;
        if (next && next.sectionOrder.length > 0) {
          setMenu(next);
          setError(null);
          setSource("payload");
        } else {
          setMenu(null);
          setError("Menu unavailable");
          setSource("payload");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching eat-and-play menu:", err);
        setError(err?.message || "Failed to load eat-and-play menu");
        setMenu(null);
        setSource("payload");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldFetch, normalizedLocale, categoryKey]);

  return { menu, loading, error, source };
}
